import { CopyableTableCell } from "@/components/typography/dfp-typography";
import { DB_ORG } from "@/constants/db";
import { Skeleton, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { LocationActions } from "./children/LocationActions";

/** Health score color logic (matches assets) */
const getHealthColor = (score: number) => {
	if (score >= 75) return "#3f6e01";
	if (score >= 25) return "#e0a724";
	return "#bf1e2d";
};

/** Smaller font render for secondary detail columns */
const SmallText = ({ text, isLoading }: { text: string; isLoading?: boolean }) => {
	if (isLoading) return <Skeleton.Input style={{ width: 80 }} active size="small" />;
	return (
		<span style={{ fontSize: 12, color: "#595959", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
			{text || "N/A"}
		</span>
	);
};

export const columns: ColumnsType = [
	{
		title: "Address",
		dataIndex: "address1",
		key: "address1",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		filterMode: "tree",
		filterSearch: true,
		onFilter: (value, record) => true,
		render: (text, record) => {
			if (record.isLoading) {
				return <Skeleton.Input style={{ width: 160 }} active size="small" />;
			}
			const healthScore = record?.health_score?.health_score ?? 0;
			const borderColor = getHealthColor(healthScore);

			return (
				<Tooltip
					title={
						<div>Health Score: <strong>{Math.round(healthScore)}</strong></div>
					}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							borderLeft: `4px solid ${borderColor}`,
							paddingLeft: 8,
							marginLeft: -12,
						}}
					>
						<span style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{text || "N/A"}
						</span>
					</div>
				</Tooltip>
			);
		},
		width: "30%",
	},
	{
		title: "Store #",
		dataIndex: "store_number",
		key: "store_number",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => (
			<CopyableTableCell text={text || "-"} isLoading={record.isLoading} />
		),
		width: "12%",
	},
	{
		title: "City",
		dataIndex: "city",
		key: "city",
		width: "15%",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => (
			<SmallText text={text} isLoading={record.isLoading} />
		),
	},
	{
		title: "State",
		dataIndex: "state",
		key: "state",
		width: "8%",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => (
			<SmallText text={text} isLoading={record.isLoading} />
		),
	},
	{
		title: "Zip",
		dataIndex: "zip",
		key: "zip",
		width: "8%",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => (
			<SmallText text={text} isLoading={record.isLoading} />
		),
	},
	{
		title: "Assets",
		dataIndex: "submission_count",
		key: "submission_count",
		width: "8%",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		align: "center",
		render: (text, record) => (
			<CopyableTableCell
				text={text ? String(text) : "0"}
				isLoading={record.isLoading}
			/>
		),
	},
	{
		title: "Actions",
		key: "actions",
		width: 80,
		render: (_, record) => <LocationActions record={record} />,
	},
];

export const cushmanColumns: ColumnsType = [
	{
		title: "Site Name",
		dataIndex: "name",
		key: "name",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		filterMode: "tree",
		filterSearch: true,
		onFilter: (value, record) => true,
		render: (text, record) => {
			if (record.isLoading) {
				return <Skeleton.Input style={{ width: 160 }} active size="small" />;
			}
			const healthScore = record?.health_score?.health_score ?? 0;
			const borderColor = getHealthColor(healthScore);

			return (
				<Tooltip
					title={
						<div>Health Score: <strong>{Math.round(healthScore)}</strong></div>
					}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							borderLeft: `4px solid ${borderColor}`,
							paddingLeft: 8,
							marginLeft: -12,
						}}
					>
						<span style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{text || "N/A"}
						</span>
					</div>
				</Tooltip>
			);
		},
		width: "25%",
	},
	{
		title: "Store #",
		dataIndex: "store_number",
		key: "store_number",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => (
			<CopyableTableCell text={text || "-"} isLoading={record.isLoading} />
		),
		width: "12%",
	},
	{
		title: "City",
		dataIndex: "city",
		key: "city",
		width: "15%",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => (
			<SmallText text={text} isLoading={record.isLoading} />
		),
	},
	{
		title: "State",
		dataIndex: "state",
		key: "state",
		width: "8%",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => (
			<SmallText text={text} isLoading={record.isLoading} />
		),
	},
	{
		title: "Zip",
		dataIndex: "zip",
		key: "zip",
		width: "8%",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => (
			<SmallText text={text} isLoading={record.isLoading} />
		),
	},
	{
		title: "Assets",
		dataIndex: "submission_count",
		key: "submission_count",
		width: "8%",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		align: "center",
		render: (text, record) => (
			<CopyableTableCell
				text={text ? String(text) : "0"}
				isLoading={record.isLoading}
			/>
		),
	},
	{
		title: "Actions",
		key: "actions",
		width: 80,
		render: (_, record) => <LocationActions record={record} />,
	},
];

export const getOrgColumns = (organization: any): ColumnsType => {
	switch (organization) {
		case DB_ORG.CUSHMANWAKEFIELD:
			return cushmanColumns;
		default:
			return columns;
	}
};
